import { ConflictErrorCode, ValidationErrorCode } from '@today/types'
import { ErrorStateVariant } from '@today/ui-components'
import { isAxiosError } from 'axios'

/** How the wizard presents a failed onboarding write (TO-733 REQ-006). */
export interface OnboardingSaveFailure {
	variant: ErrorStateVariant
	/** Plain copy for a refusal; `undefined` uses the ErrorState variant's default message. */
	message: string | undefined
	/** Whether re-sending the same write could succeed — only then is Retry offered. */
	retryable: boolean
}

/**
 * The step-update refusals the wizard explains. Re-sending the same write would be
 * refused again, so neither offers Retry. Any other code is never rendered — the
 * server's string is not ours to show.
 */
const REFUSAL_COPY: ReadonlyMap<string, string> = new Map<string, string>([
	[ConflictErrorCode.ONBOARDING_STEP_TRANSITION_INVALID, "This step is already complete and can't be changed."],
	[ValidationErrorCode.ONBOARDING_STEP_NOT_SKIPPABLE, "This step is required, so it can't be done later."],
])

/** An HTTP client error that never got a response: offline, timeout, DNS. */
const isNetworkFailure = (error: unknown): boolean => isAxiosError(error) && error.response === undefined

/** The BFF's machine-readable `error.code`, if the response carries one. */
const errorCode = (error: unknown): unknown =>
	isAxiosError<{ error?: { code?: unknown } }>(error) ? error.response?.data?.error?.code : undefined

/** TO-733 REQ-006: a failed onboarding read is either unreachable (network) or the server's failure. */
export function classifyLoadError(error: unknown): ErrorStateVariant {
	return isNetworkFailure(error) ? ErrorStateVariant.NETWORK : ErrorStateVariant.SERVER
}

/** TO-733 REQ-006: the ErrorState variant, copy and retryability of a failed step update. */
export function classifySaveError(error: unknown): OnboardingSaveFailure {
	const code = errorCode(error)
	const refusal = typeof code === 'string' ? REFUSAL_COPY.get(code) : undefined
	if (refusal) return { variant: ErrorStateVariant.VALIDATION, message: refusal, retryable: false }
	return { variant: classifyLoadError(error), message: undefined, retryable: true }
}
